import { useCallback } from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import { formatDateAt } from "simplydo/core/dates";
import { OpenAPI } from "simplydo/interfaces";
import { RichTextViewer } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, Icon, Popup } from "simplydo/react/ui";

import util from "utils/utils";

import type { FeedActivityPostItem } from "../types";
import { CardFeed, ContentArea } from "./FeedCard";

const CardHeader = styled.div`
  width: 100%;
  padding-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .author {
    display: flex;
    align-items: center;
    color: gray;
    max-width: 80%;
  }
  .header-right {
    display: flex;
    align-items: center;
    .ui.button {
      margin-left: 5px;
    }
    .created {
      > span {
        display: block;
        color: "rgb(180,180,180)";
        font-size: 10px;
      }
    }
  }
`;

const CardAvatar = styled.div<{ $avatarUrl: string }>`
  position: relative;
  height: 30px !important;
  width: 30px !important;
  line-height: inherit;
  border-radius: 50%;
  display: inline-block;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  vertical-align: middle;
  margin-right: 5px;
  background-image: ${({ $avatarUrl }) => `url(${$avatarUrl})`};
`;

const PostCardFeed = styled(CardFeed)`
  margin-bottom: 20px;
  &:hover {
    background-color: #fff;
  }
`;

type UserWithOwnerOrganisation = OpenAPI.Schemas["PreparedUser"] & {
  ownerOrganisation?: OpenAPI.Schemas["Organisation"];
};

type OrganisationPostProps = {
  user?: UserWithOwnerOrganisation;
  post: FeedActivityPostItem;
  canPost?: boolean;
  onUpdatePost?: (postId: string, updateData: Record<string, unknown>) => void;
  onDeletePost?: (postId: string) => void;
  isGroup?: boolean;
  isGroupFeed?: boolean;
  toLink?: string | null;
};

const OrganisationPost = ({
  user,
  post,
  canPost,
  onUpdatePost,
  onDeletePost,
  isGroup,
  isGroupFeed,
  toLink,
}: OrganisationPostProps) => {
  const { message, messageHtml, owner, createdAt } = post;
  const postContent = messageHtml || message || "";
  const ownerGroup =
    post.type === "groupActivityFeedPost" ? post.ownerGroup : undefined;
  const { t } = useTranslation();
  const theme = useTheme();

  const handleDelete = useCallback(() => {
    util
      .confirm(
        "Delete activity feed post",
        "The post will be deleted and no longer shown in any activity feeds in this organisation. This action is irreversible",
      )
      .then(() => onDeletePost?.(post._id))
      .catch(() => {});
  }, [onDeletePost, post._id]);

  return (
    <PostCardFeed>
      <CardHeader>
        <div className="author">
          {owner || !isGroup ?
            <CardAvatar
              $avatarUrl={util.mixinCssUrlFallback(
                util.avatarUrl(owner ?? undefined),
                util.avatarUrl(),
              )}
            />
          : null}
          {owner ?
            `${owner.profile?.fullName} posted a message to ${isGroup ? ownerGroup?.name || "this group" : user?.ownerOrganisation?.name}`
          : `${isGroup ? ownerGroup?.name || "This group" : user?.ownerOrganisation?.name} posted a message`
          }
        </div>
        <div className="header-right">
          {!theme.sizes.isMobile && (
            <div className="created">
              <span>{formatDateAt(createdAt)}</span>
            </div>
          )}
          {!canPost && post.isPinned ?
            <Button size="mini" icon="pin" disabled color="green" />
          : null}
          {canPost && onUpdatePost && (
            <Popup
              on="hover"
              hoverable
              content={
                post.isPinned ?
                  "Click this button to un-pin this post. It will no longer be guaranteed to appear at the top of every users activity feed. Once this post is one week old, it will gradually be replaced by more recent posts."
                : "Click this button to pin this post. It will always appear at the top of every users activity feed. If there are multiple pinned posts, they will be sorted by recency."
              }
              trigger={
                <Button
                  size="mini"
                  icon="pin"
                  onClick={() =>
                    onUpdatePost(post._id, { isPinned: !post.isPinned })
                  }
                  color={post.isPinned ? "green" : undefined}
                  basic={!post.isPinned}
                />
              }
            />
          )}
          {canPost && onDeletePost && (
            <Button size="mini" icon="trash" onClick={handleDelete} basic />
          )}
        </div>
      </CardHeader>
      <ContentArea>
        <RichTextViewer value={postContent} />
        {!isGroupFeed && isGroup && toLink && (
          <Link
            className="hover-action"
            to={toLink}
            style={{
              fontWeight: "bold",
              minWidth: "max-content",
              marginLeft: 8,
              marginRight: "-9px",
            }}
          >
            {t("generic.viewType", { entity: "" }).toUpperCase()}{" "}
            <Icon name="arrow alternate circle right outline" />
          </Link>
        )}
      </ContentArea>
    </PostCardFeed>
  );
};

const mapStateToProps = (state: { user?: UserWithOwnerOrganisation }) => ({
  user: state.user,
});

export default connect(mapStateToProps)(OrganisationPost);
